import { styled } from 'linaria/react';
import React, { useEffect, useState } from 'react';
import { useStockNotifications } from '@jetshop/core/hooks/Subscriptions/useStockNotifications';
import { useIntl } from '@jetshop/intl';
import UIButton from '../../../components/ui/Button';
import { ReactComponent as CloseIcon } from '../../../assets/svg/Cross.svg';
import { theme } from '../../../components/Theme';

const Button = styled(UIButton)`
  width: 100%;
  margin-bottom: 20px;
`;

const InputButton = styled(UIButton)`
  padding: 0;
  width: auto;
  min-width: 4rem;
  border-radius: 0 8px 8px 0;
  background-color: ${theme.colorScheme.primary};
  color: ${theme.colorScheme.primarytext};
`;
const Error = styled('p')`
  color: #eb5757;
  font-size: 0.875rem;
  position: absolute;
  top: 50px;
`;

const Form = styled.form`
  display: flex;
  border: 1px solid #bfbdbd;
  padding: 0;
  position: absolute;
  border-radius: 8px;
  top: 0;
  width: 100%;
  opacity: 1;
  overflow: hidden;
  &:disabled {
    opacity: 0.5;
  }

  input,
  button {
    height: 3rem;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    border: 0;
    padding: 0 0.25rem;
    :focus {
      outline: none;
    }
  }
  input {
    flex: 1 1 auto;
    padding: 0 0.5rem 0 1rem;
    border-radius: 3rem 0 0 3rem;
  }

  button {
    flex: 0 0 auto;
    justify-content: center;
    padding-right: 0.5rem;
  }
`;

const Wrapper = styled('div')`
  position: relative;
  width: 100%;
  opacity: 0;
  transition: opacity 0.1s linear;
  margin-top: 0.75rem;
  &.loaded {
    opacity: 1;
  }
  [data-visible='false'] {
    opacity: 0;
    transform: scaleY(0);
  }
  [data-visible='true'] {
    opacity: 1;
    transform: scaleY(1);
  }

  [data-visible='true'],
  [data-visible='false'] {
    transition: all 0.1s linear;
  }
  .toggle-close {
    position: absolute;
    right: 4.5rem;
  }
`;

const ToggleButton = styled.div`
  button.notify-me {
    position: relative;
    overflow: hidden;
    background: linear-gradient(
      ${theme.colorScheme.addtocartswipeangle},
      ${theme.colorScheme.addtocartlight},
      ${theme.colorScheme.addtocartdark}
    );
    color: ${theme.colorScheme.addtocarttext};
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    svg path {
      fill: ${theme.colorScheme.addtocarttext};
    }
  }

  button.notify-me:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    border: none;
    color: ${theme.colorScheme.addtocarttext};
    svg path {
      fill: ${theme.colorScheme.addtocarttext};
    }
  }

  /* Idle streak: Moves continuously left to right */
  button.notify-me::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(${theme.colorScheme.addtocartswipe}, 0) 10%,
      rgba(${theme.colorScheme.addtocartswipe}, 0.5) 50%,
      rgba(${theme.colorScheme.addtocartswipe}, 0) 90%
    );
    filter: blur(8px);
    transform: skewX(-20deg);
    animation: streak 13s linear infinite;
  }

  /* Hover streak: Bursts from center and expands outwards */
  button.notify-me::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0%;
    height: 30%;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0) 50%
    );
    transform: translate(-50%, -50%);
    opacity: 0;
  }
  button.notify-me:hover::after {
    animation: ripple 1s forwards;
  }

  /* Keyframes for the hover ripple effect */
  @keyframes ripple {
    0% {
      width: 0%;
      height: 30%;
      opacity: 1;
    }
    100% {
      width: 300%;
      height: 300%;
      opacity: 0;
    }
  }

  /* Keyframes for the idle streak */
  @keyframes streak {
    0% {
      left: -100%;
    }
    23.08% {
      /* Streak moves for 3s out of 13s total */
      left: 150%;
    }
    100% {
      left: 150%;
    }
  }
`;

const Completed = styled.div`
  position: absolute;
  top: 0;
  font-weight: 600;
  height: 3rem;
  display: flex;
  align-items: center;
`;

function NotifyWhenBack({ articleNumber }) {
  const [loaded, setLoaded] = useState();
  const {
    toggle,
    submit,
    inputProps,
    active,
    inactive,
    submitting,
    submitted,
    failed
  } = useStockNotifications({ articleNumber });
  useEffect(() => {
    setLoaded(true);
  }, []);
  const t = useIntl();
  const disabled = inputProps.value.length === 0 || submitting;

  return (
    <>
      <Wrapper className={loaded && 'loaded'}>
        <ToggleButton className="toggle-button" data-visible={inactive}>
          <Button
            className={'notify-me'}
            hideArrow
            onClick={toggle}
            data-testid="notify-when-back-button"
          >
            {t('Notify me when back in stock')}
          </Button>
        </ToggleButton>
        {failed && (
          <Error>
            {t('Something went wrong. Please check your email and try again.')}
          </Error>
        )}
        <Form
          onSubmit={submit}
          data-visible={active || failed}
          disabled={disabled}
        >
          <input
            type="email"
            id="stock-notifications"
            data-testid="notify-when-back-input"
            aria-label={t('E-mail address')}
            placeholder={t('E-mail address')}
            required
            {...inputProps}
          />
          <InputButton
            hideArrow
            rounded={false}
            data-testid="notify-when-back-ok"
            disabled={disabled}
          >
            {t('OK')}
          </InputButton>
          <Button hideArrow plain onClick={toggle} className="toggle-close">
            <CloseIcon />
          </Button>
        </Form>
        <Completed data-visible={submitted}>
          <p data-testid="notify-when-back-success">
            {t('You are now subscribed.')}
          </p>
        </Completed>
      </Wrapper>
    </>
  );
}

export default NotifyWhenBack;
